import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import ListToast from './ListToast';
import { prepareImageForSearch } from '../utils/imageSearchPrep';
import { sanitizeUserFacingError } from '../utils/userFacingError';
import {
  markSearchInputFocusPending,
  consumeSearchInputFocusPending,
  clearSearchInputFocusPending,
} from '../utils/searchInputFocus';
import BrowseDealsMenu from './BrowseDealsMenu';
import './Header.css';

const Header = ({
  onSearch,
  onImageSearch,
  searchQuery = '',
  onNotificationsClick,
  onLoginClick,
  onOrdersClick,
  onCartClick,
  cartItemCount = 0,
  myListItemCount = 0,
  unreadNotificationCount = 0,
  imageSearchActive = false,
  isLoggedIn = false,
  userInfo = null,
  onLogout = null,
  hideCart = false,
  onHomeClick,
}) => {
  const searchInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const prevListCountRef = useRef(myListItemCount);
  const [listBadgePulse, setListBadgePulse] = useState(false);
  const [imageSearchToastVisible, setImageSearchToastVisible] = useState(false);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    if (myListItemCount > prevListCountRef.current) {
      setListBadgePulse(true);
      const timer = setTimeout(() => setListBadgePulse(false), 550);
      prevListCountRef.current = myListItemCount;
      return () => clearTimeout(timer);
    }
    prevListCountRef.current = myListItemCount;
    return undefined;
  }, [myListItemCount]);

  const handleHomeNav = (e) => {
    if (onHomeClick) {
      onHomeClick(e);
      return;
    }
    // Fallback: allow Link default navigation when handler is not provided.
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const trimmed = searchQuery.trim();
    if (trimmed) {
      const { trackSearch } = require('../utils/browsingHistory');
      trackSearch(trimmed);
    }
    onSearch(trimmed);
  };

  useEffect(() => {
    if (!consumeSearchInputFocusPending()) return;
    const input = searchInputRef.current;
    if (!input) return;
    input.focus();
    const end = input.value.length;
    input.setSelectionRange(end, end);
  }, []);

  const handleInputChange = (e) => {
    const nextValue = e.target.value;
    if (window.location.pathname !== '/') {
      markSearchInputFocusPending();
    }
    onSearch(nextValue);
  };

  const handleSearchBlur = () => {
    clearSearchInputFocusPending();
    window.setTimeout(() => {
      if (document.activeElement?.closest('.search-form')) return;
      document.documentElement.classList.remove('mobile-search-active');
    }, 120);
  };

  const handleSearchFocus = () => {
    if (!window.matchMedia('(max-width: 768px)').matches) return;
    document.documentElement.classList.add('mobile-search-active');
    window.requestAnimationFrame(() => {
      searchInputRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
  };

  useEffect(() => () => {
    document.documentElement.classList.remove('mobile-search-active');
  }, []);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file (JPG, PNG, GIF, etc.)');
      e.target.value = '';
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Image file is too large. Please upload an image smaller than 10MB.');
      e.target.value = '';
      return;
    }

    e.target.value = '';
    document.documentElement.classList.remove('mobile-search-active');
    setImageSearchToastVisible(true);

    try {
      const prepared = await prepareImageForSearch(file, { crop: 'center' });
      if (onImageSearch) {
        onImageSearch(prepared.dataUrl, prepared.file);
      }
    } catch (err) {
      setImageSearchToastVisible(false);
      alert(sanitizeUserFacingError(err.message, 'Error preparing image. Please try again.'));
    }
  };

  const handleImageButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <header className="header">
      <div className="header-container">
        <div className="header-primary">
        <div className="logo-group">
          <Link to="/" className="logo-section" aria-label="Raheasy home" onClick={handleHomeNav}>
            <img src="/RaheasY Main Logo.png" alt="Raheasy" className="logo" />
          </Link>
          <div className="header-nav-cluster">
            <Link to="/" className="home-button" onClick={handleHomeNav}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9.5L12 3l9 6.5V21a1 1 0 0 1-1 1h-6v-6H10v6H4a1 1 0 0 1-1-1V9.5z"></path>
              </svg>
              <span>Home</span>
            </Link>
            <BrowseDealsMenu />
          </div>
        </div>
        
        <form className="search-form" onSubmit={handleSearch}>
          <input
            ref={searchInputRef}
            type="text"
            className="search-input"
            placeholder="Search deals, products, stores or brands"
            value={searchQuery}
            onChange={handleInputChange}
            onBlur={handleSearchBlur}
            onFocus={handleSearchFocus}
          />
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            onChange={handleImageUpload}
            style={{ display: 'none' }}
          />
          <button 
            type="button" 
            className={`image-search-button ${imageSearchActive ? 'active' : ''}`}
            onClick={handleImageButtonClick}
            title="Search by image"
            aria-label="Search by image"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <circle cx="8.5" cy="8.5" r="1.5"></circle>
              <path d="M21 15l-5-5L5 21"></path>
            </svg>
            {imageSearchActive && <span className="image-search-badge">●</span>}
          </button>
          <button type="submit" className="search-submit-button">Find Deals</button>
        </form>
        </div>

        <div className="header-actions">
          <button
            className="header-action-item theme-toggle"
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
          >
            {theme === 'dark' ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="5"/>
                <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
            )}
            <span className="action-label">{theme === 'dark' ? 'Light' : 'Dark'}</span>
          </button>
          {isLoggedIn && (
          <button 
            className="header-action-item" 
            aria-label="Notifications"
            onClick={onNotificationsClick}
          >
            <div className="action-icon-wrapper">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
              </svg>
              {unreadNotificationCount > 0 && (
                <span className="notification-badge">{unreadNotificationCount}</span>
              )}
            </div>
            <span className="action-label">Notifications</span>
          </button>
          )}
          {isLoggedIn && userInfo ? (
            <div className="user-menu">
              <div className="user-info">
                {userInfo.picture && (
                  <img 
                    src={userInfo.picture} 
                    alt={userInfo.name || 'User'} 
                    className="user-avatar"
                  />
                )}
                <span className="user-name">{userInfo.name || userInfo.email || 'User'}</span>
              </div>
              {onLogout && (
                <button 
                  className="header-action-item logout-btn" 
                  aria-label="Log out"
                  onClick={onLogout}
                  title="Log out"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                  <span className="action-label">Log out</span>
                </button>
              )}
            </div>
          ) : (
            <button 
              className="header-action-item" 
              aria-label="Log in"
              onClick={onLoginClick}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              <span className="action-label">Log in</span>
            </button>
          )}
          <button 
            className="header-action-item header-action-item--saved-deals" 
            aria-label="Saved deals"
            onClick={onOrdersClick}
          >
            <div className="action-icon-wrapper">
              <svg
                className="header-saved-deals-icon"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
              {myListItemCount > 0 && (
                <span className={`cart-badge${listBadgePulse ? ' cart-badge--pulse' : ''}`}>
                  {myListItemCount}
                </span>
              )}
            </div>
            <span className="action-label">Saved Deals</span>
          </button>
          {!hideCart && (
          <button 
            className="header-action-item" 
            aria-label="Shopping cart"
            onClick={onCartClick}
          >
            <div className="action-icon-wrapper">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
              {cartItemCount > 0 && (
                <span className="cart-badge">{cartItemCount}</span>
              )}
            </div>
            <span className="action-label">Shopping cart</span>
          </button>
          )}
        </div>
      </div>
      <ListToast
        visible={imageSearchToastVisible}
        message="Preparing image search…"
        onClose={() => setImageSearchToastVisible(false)}
      />
    </header>
  );
};

export default Header;

